
<template>
  <div class="main-content">
    <Container
      :title="t('route.materialCategoryUser')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          ref="searchFormRef"
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcMaterialCategoryUserEdit'])"
          @click="handleCreate()"
          type="success"
        >
          {{ t('table.add') }}
        </el-button>
        <el-button
          v-if="selection.length > 0 && this.$hasPermission(['ACTION_vcMaterialCategoryUserEdit'])"
          @click="handleBathDelete()"
          type="danger"
        >
          {{ t('table.bathDelete') }}
        </el-button>
        <SimpleTable
          :isLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :sort="sort"
          :order="order"
          @handleOrder="handleOrder"
          @handleSelection="handleSelection"
        >
          <template
            v-if="this.$hasPermission(['ACTION_vcMaterialCategoryUserEdit'])"
            #default="slotProps"
          >
            <el-button
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
            <el-button
              @click="handleDelete(slotProps.row.id)"
              type="danger"
              size="mini"
            >
              {{ t('common.delete') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <el-dialog
      v-if="this.$hasPermission(['ACTION_vcMaterialCategoryUserEdit'])"
      :title="t(textMap[dialogStatus])"
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NomalContainer>
        <CenterForm
          v-if="dialogStatus === 'create'"
          :formModel="createModel"
          :formStruct="createStruct"
          :formRules="materialCategoryUserRole"
          @submit="createSubmit"
          @reset="createReset"
        />
        <CenterForm
          v-if="dialogStatus === 'update'"
          :formModel="updateModel"
          :formStruct="updateStruct"
          :formRules="materialCategoryUserRole"
          @submit="updateNameSubmit"
          @reset="updateNameReset"
        />
      </NomalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  MaterialCategoryUserModel,
  MaterialCategoryUserPageSearchModel,
  MaterialCategoryUserSearchModel,
  MaterialCategoryUserAddModel,
  MaterialCategoryUserUpdateModel,
  IdsModel
} from '@/model/product/MaterialCategoryUserModel'
import {
  pageSearchMaterialCategoryUser,
  addMaterialCategoryUser,
  updateMaterialCategoryUser,
  removeMaterialCategoryUser,
  batchRemoveMaterialCategoryUser
} from '@/apis/product/materialCategoryUser'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'categoryName', label: t('materialCategoryUser.categoryName'), placeholder: t('materialCategoryUser.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'userId', label: t('materialCategoryUser.userId'), placeholder: t('materialCategoryUser.userIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'displayOrder', label: t('materialCategoryUser.displayOrder'), placeholder: t('materialCategoryUser.displayOrderP'), component: 'el-input', type: 'number', model: null },
      { prop: 'clientId', label: t('materialCategoryUser.clientId'), placeholder: t('materialCategoryUser.clientIdP'), component: 'el-input', type: 'text', model: null }
    ]

    const createStruct = [
      { prop: 'categoryName', label: t('materialCategoryUser.categoryName'), placeholder: t('materialCategoryUser.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'userId', label: t('materialCategoryUser.userId'), placeholder: t('materialCategoryUser.userIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'displayOrder', label: t('materialCategoryUser.displayOrder'), placeholder: t('materialCategoryUser.displayOrderP'), component: 'el-input', type: 'number', model: null },
      { prop: 'clientId', label: t('materialCategoryUser.clientId'), placeholder: t('materialCategoryUser.clientIdP'), component: 'el-input', type: 'text', model: null }
    ]

    const updateStruct = [
      { prop: 'categoryName', label: t('materialCategoryUser.categoryName'), placeholder: t('materialCategoryUser.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'userId', label: t('materialCategoryUser.userId'), placeholder: t('materialCategoryUser.userIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'displayOrder', label: t('materialCategoryUser.displayOrder'), placeholder: t('materialCategoryUser.displayOrderP'), component: 'el-input', type: 'number', model: null },
      { prop: 'clientId', label: t('materialCategoryUser.clientId'), placeholder: t('materialCategoryUser.clientIdP'), component: 'el-input', type: 'text', model: null }
    ]

    const materialCategoryUserRole = {
      categoryName: [{ required: true, message: t('materialCategoryUser.categoryName').concat(t('common.isRequired')), trigger: 'blur' }],
      userId: [{ required: true, message: t('materialCategoryUser.userId').concat(t('common.isRequired')), trigger: 'blur' }],
      displayOrder: [{ required: true, message: t('materialCategoryUser.displayOrder').concat(t('common.isRequired')), trigger: 'blur' }],
      clientId: [{ required: true, message: t('materialCategoryUser.clientId').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [

      { prop: 'categoryName', label: t('materialCategoryUser.categoryName'), sortable: 'custom' },
      { prop: 'userId', label: t('materialCategoryUser.userId'), sortable: 'custom' },
      { prop: 'displayOrder', label: t('materialCategoryUser.displayOrder'), sortable: 'custom' },
      { prop: 'clientId', label: t('materialCategoryUser.clientId'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: MaterialCategoryUserModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: MaterialCategoryUserModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as MaterialCategoryUserSearchModel,
      tableData: Array<MaterialCategoryUserModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'materialCategoryUser.edit',
        create: 'materialCategoryUser.create'
      },
      dialogFormVisible: false,
      materialCategoryUserRole: materialCategoryUserRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as MaterialCategoryUserAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as MaterialCategoryUserUpdateModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: MaterialCategoryUserPageSearchModel = {
          categoryName: dataMap.searchFormModel.categoryName,
          userId: dataMap.searchFormModel.userId,
          displayOrder: dataMap.searchFormModel.displayOrder,
          clientId: dataMap.searchFormModel.clientId,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchMaterialCategoryUser(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: MaterialCategoryUserAddModel = {
          categoryName: dataMap.createModel.categoryName,
          userId: dataMap.createModel.userId,
          displayOrder: dataMap.createModel.displayOrder,
          clientId: dataMap.createModel.clientId
        }
        const result = await addMaterialCategoryUser(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: MaterialCategoryUserUpdateModel = {
          id: dataMap.updateModel.id,
          categoryName: dataMap.updateModel.categoryName,
          userId: dataMap.updateModel.userId,
          displayOrder: dataMap.updateModel.displayOrder,
          clientId: dataMap.updateModel.clientId
        }
        const result = await updateMaterialCategoryUser(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeMaterialCategoryUser(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveMaterialCategoryUser(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
      },
      createReset() {
        console.log('create reset')
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: MaterialCategoryUserModel) => {
      if (row) {
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.categoryName = row.categoryName
        dataMap.updateModel.userId = row.userId
        dataMap.updateModel.displayOrder = row.displayOrder
        dataMap.updateModel.clientId = row.clientId
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      ...toRefs(dataMap)
    }
  }
})
</script>
